/**
 * OverviewTabView - Layout component for the Overview tab content
 *
 * On desktop: arranges PlayerSummaryCard and PlayerStatsTable side by side.
 * On mobile: stacks all components in a single column.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { memo } from "react";

import { Stack } from "@/components/atoms/Stack";

import type { PlayerDetailPlayerMetaFragment } from "@/generated/graphql";

import PlayerSummaryCard from "@/features/player-detail/components/organisms/PlayerSummaryCard";
import PlayerStatsTable from "@/features/player-detail/components/organisms/PlayerStatsTable";
import RecentPerformances from "@/features/player-detail/components/organisms/RecentPerformances";

/**
 * Props for OverviewTabView component
 */
interface OverviewTabViewProps {
  /** Whether dark mode is active */
  isDark: boolean;
  /** Player data fragment */
  player?: PlayerDetailPlayerMetaFragment | null;
  /** Whether the screen is mobile sized */
  isSmallScreen: boolean;
  /** Whether to use compact spacing (used in modal context) */
  isCompact?: boolean;
}

/**
 * OverviewTabView renders the Overview tab layout
 * @param props - View props
 * @param props.isDark - Whether dark mode is active
 * @param props.player - Player data fragment
 * @param props.isSmallScreen - Whether the screen is mobile sized
 * @param props.isCompact - Whether to use compact spacing (used in modal context)
 * @returns VStack containing the overview tab content
 */
const OverviewTabView = ({
  isDark,
  player,
  isSmallScreen,
  isCompact = false,
}: OverviewTabViewProps) => (
  <Stack
    className={`w-full ${isSmallScreen ? "gap-3 pt-2" : isCompact ? "gap-3 pt-2" : "gap-4 pt-4"}`}
  >
    {isSmallScreen ? (
      <>
        <PlayerSummaryCard isDark={isDark} player={player} />
        <PlayerStatsTable isDark={isDark} player={player} />
      </>
    ) : (
      <Stack direction="horizontal" className="w-full gap-4">
        {/* basis-7/12 + basis-5/12 = the closed-token 7:5 (58.3/41.7) split that
            replaces the off-scale 1.2-to-1 flex-grow ratio (SE-5292; snap-table
            migrate-by-hand). Stack has no default flex-grow and base min-w-0, so
            the gap shrinks both columns proportionally to basis, holding the ratio. */}
        <Stack className="basis-7/12">
          <PlayerSummaryCard isDark={isDark} player={player} />
        </Stack>
        <Stack className="basis-5/12">
          <PlayerStatsTable isDark={isDark} player={player} />
        </Stack>
      </Stack>
    )}
    <RecentPerformances isDark={isDark} player={player} />
  </Stack>
);

OverviewTabView.displayName = "OverviewTabView";

export default memo(OverviewTabView);
